<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.root"
  >
    <div
      :disabled="disabled"
      :freeze="freeze"
      :selected="selected"
      :class="$style.container"
      :style="style.container"
    >
      <div
        :disabled="disabled"
        :freeze="freeze"
        :selected="selected"
        :class="$style.state"
        :style="style.state"
      >
        <slot name="state" />
      </div>
      <div
        :disabled="disabled"
        :freeze="freeze"
        :selected="selected"
        :class="$style.text"
        :style="style.text"
      >
        <slot name="text" />
      </div>
      <div
        :disabled="disabled"
        :freeze="freeze"
        :selected="selected"
        :class="$style.controller"
        :style="style.controller"
      >
        <slot
          :disabled="disabled"
          :freeze="freeze"
          :selected="selected"
          name="controller"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { CssStyleProcessor } from '@js/vue-style-utils'

  export default {
    name: 'BaseTag',
    inheritAttrs: false,
    props: {
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      freeze: {
        type: Boolean,
        required: false,
        default: false,
      },
      selected: {
        type: Boolean,
        required: false,
        default: false,
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data: function () {
      return {
        style: {
          container: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            whiteSpace: 'nowrap',
          },
          controller: {
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
          },
          root: {},
          state: {
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
          },
          text: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
          },
        },
      };
    },
    created: function () {
      const proc = new CssStyleProcessor('--v2-base-tag', ['selected']);

      Object.assign(
        this.style.container,
        proc.getExtendedStyle(this.styleConfig.container, 'container'),
      );
      Object.assign(
        this.style.controller,
        proc.getExtendedStyle(this.styleConfig.controller, 'controller'),
      );
      Object.assign(
        this.style.state,
        proc.getExtendedStyle(this.styleConfig.state, 'state'),
      );
      Object.assign(
        this.style.text,
        proc.getExtendedStyle(this.styleConfig.text, 'text'),
      );

      Object.assign(
        this.style.root,
        this.styleConfig.root,
      );
    },
  };
</script>

<style lang="stylus" module>
.container
  background-color var(--v2-base-tag-container-background-color)
  border var(--v2-base-tag-container-border)
  border-radius var(--v2-base-tag-container-border-radius)
  border-width var(--v2-base-tag-container-border-width)
  box-shadow var(--v2-base-tag-container-box-shadow)
  cursor var(--v2-base-tag-container-cursor)
  &[selected], &[selected]:hover
    background-color var(--v2-base-tag-container-background-color-selected)
    border var(--v2-base-tag-container-border-selected)
    border-radius var(--v2-base-tag-container-border-radius-selected)
    border-width var(--v2-base-tag-container-border-width-selected)
    box-shadow var(--v2-base-tag-container-box-shadow-selected)
    cursor var(--v2-base-tag-container-cursor-selected)
  &:hover
    background-color var(--v2-base-tag-container-background-color-hover)
    border var(--v2-base-tag-container-border-hover)
    border-radius var(--v2-base-tag-container-border-radius-hover)
    border-width var(--v2-base-tag-container-border-width-hover)
    box-shadow var(--v2-base-tag-container-box-shadow-hover)
    cursor var(--v2-base-tag-container-cursor-hover)
  &[disabled]
    background-color var(--v2-base-tag-container-background-color-disabled)
    border var(--v2-base-tag-container-border-disabled)
    border-radius var(--v2-base-tag-container-border-radius-disabled)
    border-width var(--v2-base-tag-container-border-width-disabled)
    box-shadow var(--v2-base-tag-container-box-shadow-disabled)
    cursor var(--v2-base-tag-container-cursor-disabled)
  &[freeze], &[freeze]:hover
    background-color var(--v2-base-tag-container-background-color)
    border var(--v2-base-tag-container-border)
    border-radius var(--v2-base-tag-container-border-radius)
    border-width var(--v2-base-tag-container-border-width)
    box-shadow var(--v2-base-tag-container-box-shadow)
    cursor var(--v2-base-tag-container-cursor-disabled)
  &[freeze][selected]
    background-color var(--v2-base-tag-container-background-color-selected)
    border var(--v2-base-tag-container-border-selected)
    border-radius var(--v2-base-tag-container-border-radius-selected)
    border-width var(--v2-base-tag-container-border-width-selected)
    box-shadow var(--v2-base-tag-container-box-shadow-selected)
    cursor var(--v2-base-tag-container-cursor-disabled)

.controller
  background-color var(--v2-base-tag-controller-background-color)
  border var(--v2-base-tag-controller-border)
  border-radius var(--v2-base-tag-controller-border-radius)
  border-width var(--v2-base-tag-controller-border-width)
  box-shadow var(--v2-base-tag-controller-box-shadow)
  color var(--v2-base-tag-controller-color)
  cursor var(--v2-base-tag-controller-cursor)
  font-size var(--v2-base-tag-controller-font-size)
  font-weight var(--v2-base-tag-controller-font-weight)
  &[selected], &[selected]:hover, &[selected]:active
    background-color var(--v2-base-tag-controller-background-color-selected)
    border var(--v2-base-tag-controller-border-selected)
    border-radius var(--v2-base-tag-controller-border-radius-selected)
    border-width var(--v2-base-tag-controller-border-width-selected)
    box-shadow var(--v2-base-tag-controller-box-shadow-selected)
    color var(--v2-base-tag-controller-color-selected)
    cursor var(--v2-base-tag-controller-cursor-selected)
    font-size var(--v2-base-tag-controller-font-size-selected)
    font-weight var(--v2-base-tag-controller-font-weight-selected)
  &:hover
    background-color var(--v2-base-tag-controller-background-color-hover)
    border var(--v2-base-tag-controller-border-hover)
    border-radius var(--v2-base-tag-controller-border-radius-hover)
    border-width var(--v2-base-tag-controller-border-width-hover)
    box-shadow var(--v2-base-tag-controller-box-shadow-hover)
    color var(--v2-base-tag-controller-color-hover)
    cursor var(--v2-base-tag-controller-cursor-hover)
    font-size var(--v2-base-tag-controller-font-size-hover)
    font-weight var(--v2-base-tag-controller-font-weight-hover)
  &:active
    background-color var(--v2-base-tag-controller-background-color-active)
    border var(--v2-base-tag-controller-border-active)
    border-radius var(--v2-base-tag-controller-border-radius-active)
    border-width var(--v2-base-tag-controller-border-width-active)
    box-shadow var(--v2-base-tag-controller-box-shadow-active)
    color var(--v2-base-tag-controller-color-active)
    cursor var(--v2-base-tag-controller-cursor-active)
    font-size var(--v2-base-tag-controller-font-size-active)
    font-weight var(--v2-base-tag-controller-font-weight-active)
  &[disabled]
    background-color var(--v2-base-tag-controller-background-color-disabled)
    border var(--v2-base-tag-controller-border-disabled)
    border-radius var(--v2-base-tag-controller-border-radius-disabled)
    border-width var(--v2-base-tag-controller-border-width-disabled)
    box-shadow var(--v2-base-tag-controller-box-shadow-disabled)
    color var(--v2-base-tag-controller-color-disabled)
    cursor var(--v2-base-tag-controller-cursor-disabled)
    font-size var(--v2-base-tag-controller-font-size-disabled)
    font-weight var(--v2-base-tag-controller-font-weight-disabled)
  &[freeze], &[freeze]:hover, &[freeze]:active
    background-color var(--v2-base-tag-controller-background-color)
    border var(--v2-base-tag-controller-border)
    border-radius var(--v2-base-tag-controller-border-radius)
    border-width var(--v2-base-tag-controller-border-width)
    box-shadow var(--v2-base-tag-controller-box-shadow)
    color var(--v2-base-tag-controller-color)
    cursor var(--v2-base-tag-controller-disabled)
    font-size var(--v2-base-tag-controller-font-size)
    font-weight var(--v2-base-tag-controller-font-weight)
  &[freeze][selected]
    background-color var(--v2-base-tag-controller-background-color-selected)
    border var(--v2-base-tag-controller-border-selected)
    border-radius var(--v2-base-tag-controller-border-radius-selected)
    border-width var(--v2-base-tag-controller-border-width-selected)
    box-shadow var(--v2-base-tag-controller-box-shadow-selected)
    color var(--v2-base-tag-controller-color-selected)
    cursor var(--v2-base-tag-controller-cursor-disabled)
    font-size var(--v2-base-tag-controller-font-size-selected)
    font-weight var(--v2-base-tag-controller-font-weight-selected)

.state
  background-color var(--v2-base-tag-state-background-color)
  border var(--v2-base-tag-state-border)
  border-radius var(--v2-base-tag-state-border-radius)
  border-width var(--v2-base-tag-state-border-width)
  box-shadow var(--v2-base-tag-state-box-shadow)
  color var(--v2-base-tag-state-color)
  cursor var(--v2-base-tag-state-cursor)
  font-size var(--v2-base-tag-state-font-size)
  font-weight var(--v2-base-tag-state-font-weight)
  &[selected], &[selected]:hover, &[selected]:active
    background-color var(--v2-base-tag-state-background-color-selected)
    border var(--v2-base-tag-state-border-selected)
    border-radius var(--v2-base-tag-state-border-radius-selected)
    border-width var(--v2-base-tag-state-border-width-selected)
    box-shadow var(--v2-base-tag-state-box-shadow-selected)
    color var(--v2-base-tag-state-color-selected)
    cursor var(--v2-base-tag-state-cursor-selected)
    font-size var(--v2-base-tag-state-font-size-selected)
    font-weight var(--v2-base-tag-state-font-weight-selected)
  &:hover
    background-color var(--v2-base-tag-state-background-color-hover)
    border var(--v2-base-tag-state-border-hover)
    border-radius var(--v2-base-tag-state-border-radius-hover)
    border-width var(--v2-base-tag-state-border-width-hover)
    box-shadow var(--v2-base-tag-state-box-shadow-hover)
    color var(--v2-base-tag-state-color-hover)
    cursor var(--v2-base-tag-state-cursor-hover)
    font-size var(--v2-base-tag-state-font-size-hover)
    font-weight var(--v2-base-tag-state-font-weight-hover)
  &:active
    background-color var(--v2-base-tag-state-background-color-active)
    border var(--v2-base-tag-state-border-active)
    border-radius var(--v2-base-tag-state-border-radius-active)
    border-width var(--v2-base-tag-state-border-width-active)
    box-shadow var(--v2-base-tag-state-box-shadow-active)
    color var(--v2-base-tag-state-color-active)
    cursor var(--v2-base-tag-state-cursor-active)
    font-size var(--v2-base-tag-state-font-size-active)
    font-weight var(--v2-base-tag-state-font-weight-active)
  &[disabled]
    background-color var(--v2-base-tag-state-background-color-disabled)
    border var(--v2-base-tag-state-border-disabled)
    border-radius var(--v2-base-tag-state-border-radius-disabled)
    border-width var(--v2-base-tag-state-border-width-disabled)
    box-shadow var(--v2-base-tag-state-box-shadow-disabled)
    color var(--v2-base-tag-state-color-disabled)
    cursor var(--v2-base-tag-state-cursor-disabled)
    font-size var(--v2-base-tag-state-font-size-disabled)
    font-weight var(--v2-base-tag-state-font-weight-disabled)
  &[freeze], &[freeze]:hover, &[freeze]:active
    background-color var(--v2-base-tag-state-background-color)
    border var(--v2-base-tag-state-border)
    border-radius var(--v2-base-tag-state-border-radius)
    border-width var(--v2-base-tag-state-border-width)
    box-shadow var(--v2-base-tag-state-box-shadow)
    color var(--v2-base-tag-state-color)
    cursor var(--v2-base-tag-state-cursor-disabled)
    font-size var(--v2-base-tag-state-font-size)
    font-weight var(--v2-base-tag-state-font-weight)
  &[freeze][selected]
    background-color var(--v2-base-tag-state-background-color-selected)
    border var(--v2-base-tag-state-border-selected)
    border-radius var(--v2-base-tag-state-border-radius-selected)
    border-width var(--v2-base-tag-state-border-width-selected)
    box-shadow var(--v2-base-tag-state-box-shadow-selected)
    color var(--v2-base-tag-state-color-selected)
    cursor var(--v2-base-tag-state-cursor-disabled)
    font-size var(--v2-base-tag-state-font-size-selected)
    font-weight var(--v2-base-tag-state-font-weight-selected)

.text
  color var(--v2-base-tag-text-color)
  cursor var(--v2-base-tag-text-cursor)
  font-family var(--v2-base-tag-text-font-family)
  font-size var(--v2-base-tag-text-font-size)
  font-weight var(--v2-base-tag-text-font-weight)
  text-decoration var(--v2-base-tag-text-text-decoration)
  &[selected], &[selected]:hover, &[selected]:active
    color var(--v2-base-tag-text-color-selected)
    cursor var(--v2-base-tag-text-cursor-selected)
    font-family var(--v2-base-tag-text-font-family-selected)
    font-size var(--v2-base-tag-text-font-size-selected)
    font-weight var(--v2-base-tag-text-font-weight-selected)
    text-decoration var(--v2-base-tag-text-text-decoration-selected)
  &:hover
    color var(--v2-base-tag-text-color-hover)
    cursor var(--v2-base-tag-text-cursor-hover)
    font-family var(--v2-base-tag-text-font-family-hover)
    font-size var(--v2-base-tag-text-font-size-hover)
    font-weight var(--v2-base-tag-text-font-weight-hover)
    text-decoration var(--v2-base-tag-text-text-decoration-hover)
  &:active
    color var(--v2-base-tag-text-color-active)
    cursor var(--v2-base-tag-text-cursor-active)
    font-family var(--v2-base-tag-text-font-family-active)
    font-size var(--v2-base-tag-text-font-size-active)
    font-weight var(--v2-base-tag-text-font-weight-active)
    text-decoration var(--v2-base-tag-text-text-decoration-active)
  &[disabled]
    color var(--v2-base-tag-text-color-disabled)
    cursor var(--v2-base-tag-text-cursor-disabled)
    font-family var(--v2-base-tag-text-font-family-disabled)
    font-size var(--v2-base-tag-text-font-size-disabled)
    font-weight var(--v2-base-tag-text-font-weight-disabled)
    text-decoration var(--v2-base-tag-text-text-decoration-disabled)
  &[freeze], &[freeze]:hover, &[freeze]:active
    color var(--v2-base-tag-text-color)
    cursor var(--v2-base-tag-text-cursor-disabled)
    font-family var(--v2-base-tag-text-font-family)
    font-size var(--v2-base-tag-text-font-size)
    font-weight var(--v2-base-tag-text-font-weight)
    text-decoration var(--v2-base-tag-text-text-decoration)
  &[freeze][selected]
    color var(--v2-base-tag-text-color-selected)
    cursor var(--v2-base-tag-text-cursor-disabled)
    font-family var(--v2-base-tag-text-font-family-selected)
    font-size var(--v2-base-tag-text-font-size-selected)
    font-weight var(--v2-base-tag-text-font-weight-selected)
    text-decoration var(--v2-base-tag-text-text-decoration-selected)
</style>
